import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Button, Popover } from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import apiUrl from '../../Config';

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : "";
}

const MyOrders = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTracking, setSelectedTracking] = useState({});
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();
  const token = getCookie('token');

  const handleReceiptDownload = async (receiptUrl) => {
    if (!receiptUrl || receiptUrl === '#') return;
    
    try {
      // First, try to download the file
      const response = await fetch(receiptUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });
      
      if (!response.ok) throw new Error('Failed to download receipt');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `receipt-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      // Also open in new tab
      window.open(receiptUrl, '_blank');
    } catch (error) {
      console.error('Error downloading receipt:', error);
      // Fallback to just opening in new tab if download fails
      window.open(receiptUrl, '_blank');
    }
  };

  const handleTrackingClick = (event, order) => {
    setAnchorEl(event.currentTarget);
    setSelectedTracking({
      provider: order?.deliveredvia || '----',
      id: order?.trackId || '----',
      receiptLink: order?.reciptUrl || '#'
    });
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await axios.get(`${apiUrl}/bookings/getmybooking`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setOrders(res.data.getBookings || []);
      } catch (err) {
        setOrders([]);
      }
    };
    fetchOrders();
  }, [token]);

  const handleBuyNow = async (item) => {
    try {
      if (item.comboId && item.comboOffer) {
        navigate('/checkout', { state: { ...item.comboOffer, isCombo: true } });
      } else if (item.book) {
        navigate('/checkout', { state: item.book });
      }
    } catch (error) {
      console.log("Failed to add to cart. Please login or try again.");
    }
  };

  return (
    <>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        My Orders
      </Typography>

      {orders.length === 0 ? (
        <Box sx={{
          textAlign: 'center',
          py: 4,
          bgcolor: '#fafafa',
          borderRadius: 2,
          px: 2
        }}>
          <Typography variant="body1" color="textSecondary">
            No orders found. Your orders will appear here once you make a purchase.
          </Typography>
        </Box>
      ) : (
        orders.map((order, idx) => (
          <Paper
            key={order.id || idx}
            elevation={0}
            sx={{
              border: '1.5px solid #e0e0e0',
              borderRadius: 3,
              p: { xs: 1, sm: 2, md: 0 },
              bgcolor: '#fff',
              maxWidth: { xs: '100%', md: 1000 },
              mx: 'auto',
              mb: 2,
              overflow: 'hidden'
            }}
          >
            {order.booking_items && order.booking_items.length > 0 ? order.booking_items.map((item, itemIdx) => (
              <Box
                key={itemIdx}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'flex-start' },
                  px: { xs: 0.5, sm: 2 },
                  pt: itemIdx === 0 ? 2 : 0,
                  pb: 1,
                  mb: 1,
                  gap: 2,
                  borderBottom: itemIdx === order.booking_items.length - 1 ? 'none' : '1px solid #f0f0f0'
                }}
              >
                {/* Book/Combo Image */}
                <Box
                  sx={{
                    width: { xs: '100%', sm: 65 },
                    height: { xs: 200, sm: 65 },
                    bgcolor: "#e0e0e0",
                    borderRadius: 2,
                    flexShrink: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: { xs: 'auto', sm: 0 }
                  }}
                >
                  {item.book?.bookImages?.[0]?.imageUrl ? (
                    <img
                      src={item.book.bookImages[0].imageUrl}
                      alt={item.book?.title || "Book"}
                      style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8 }}
                    />
                  ) : item.comboOffer?.imageUrl ? (
                    <img
                      src={item.comboOffer.imageUrl}
                      alt={item.comboOffer?.title || "Combo"}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                    />
                  ) : null}
                </Box>

                {/* Book/Combo Info and Actions */}
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={600} sx={{ fontSize: 15, mb: 0.5, fontFamily: 'Poppins' }}>
                    {item.book?.title || item.comboOffer?.title || 'Book Title'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 14, color: '#444', fontFamily: 'Poppins' }}>
                      Quantity: {item.quantity || item.comboOffer?.quantity || 0}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        borderRadius: 10,
                        fontSize: 13,
                        textTransform: 'none',
                        px: 2,
                        borderColor: '#bbb',
                        color: '#222',
                        fontWeight: 400,
                        bgcolor: '#fff'
                      }}
                      onClick={() => handleBuyNow(item)}
                    >
                      Buy it again
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        borderRadius: 10,
                        fontSize: 13,
                        textTransform: 'none',
                        px: 2,
                        borderColor: '#bbb',
                        color: '#222',
                        fontWeight: 400,
                        bgcolor: '#fff'
                      }}
                      onClick={() => {
                        if (item?.bookId) {
                          navigate(`/book-overview/${item.bookId}`);
                        } else if (item?.comboId) {
                          navigate(`/combo-overview/${item.comboId}`);
                        }
                      }}
                    >
                      View your item
                    </Button>
                  </Box>
                </Box>
              </Box>
            )) : (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography color="text.secondary">No items in this order.</Typography>
              </Box>
            )}

            {/* Order details bar */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexWrap: 'wrap',
                gap: 0,
                bgcolor: '#fafafa',
                borderRadius: 0,
                px: { xs: 2, sm: 5 },
                py: 1.5,
                borderTop: '1px solid #eee'
              }}
            >
              <Box sx={{ minWidth: 120 }}>
                <Typography sx={{ fontSize: 12, color: '#888', fontFamily: 'Poppins' }}>Order placed</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 12, fontFamily: 'Poppins' }}>
                  {order.bookingDate ? new Date(order.bookingDate).toLocaleDateString('en-IN', {
                    day: '2-digit', month: 'long', year: 'numeric'
                  }) : '--'}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 120 }}>
                <Typography sx={{ fontSize: 12, color: '#888', fontFamily: 'Poppins' }}>Total Amount</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 12, fontFamily: 'Poppins' }}>
                  ₹ {order.totalAmount}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 100 }}>
                <Typography sx={{ fontSize: 12, color: '#888', fontFamily: 'Poppins' }}>Address</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 12, fontFamily: 'Poppins' }}>
                  {order.userAddress?.name}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 120 }}>
                <Typography sx={{ fontSize: 12, color: '#888', fontFamily: 'Poppins' }}>Booking Status</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 12, fontFamily: 'Poppins' }}>
                  {order.bookingStatus}
                </Typography>
              </Box>
          
              <Box sx={{ minWidth: 180, ml: 'auto', textAlign: 'right' }}>
                {order.bookingStatus !== 'pending' && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => handleTrackingClick(e, order)}
                    sx={{
                      borderRadius: 20,
                      fontSize: '12px',
                      fontFamily: 'Poppins',
                      textTransform: 'none',
                      px: 2,
                      borderColor: '#ccc',
                      color: '#333',
                      backgroundColor: 'white',
                      '&:hover': {
                        borderColor: '#999'
                      }
                    }}
                  >
                    View Tracking details ▾
                  </Button>
                )}

                <Popover
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handlePopoverClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    sx: {
                      p: 2,
                    mt:1,
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      minWidth: 220,
                    },
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#666', fontSize: '12px' }}>
                    Delivery via
                  </Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', mt: 0.5 }}>
                    {selectedTracking?.provider || 'Not available'}
                  </Typography>
                  <Typography sx={{ color: '#999', fontSize: '12px', mt: 1 }}>
                    Tracking ID
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                    {selectedTracking?.id || 'Not available'}
                  </Typography>
                  <Button
                    variant="text"

                    fullWidth
                    onClick={() => handleReceiptDownload(selectedTracking.receiptLink)}
                    sx={{
                      mt: 1.5,
                      fontSize: '12px',
                      color: '#1976d2',
                      bgcolor: '#f3f3f3',
                      textTransform: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                        
                      }
                    }}
                  >
                    View & Download Receipt
                  </Button>
                </Popover>
              </Box>

            
              <Box sx={{ minWidth: 100, ml: '0', textAlign: 'right' }}>
                <Typography sx={{ fontSize: 12, color: '#888', fontFamily: 'Poppins' }}>Order ID</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 12, fontFamily: 'Poppins' }}>
                  {order.id}
                </Typography>
              </Box>
               
            </Box>
          </Paper>
        ))
      )}
    </>
  );
};

export default MyOrders;
